import React from "react";
import Form from "react-bootstrap/Form";
import { INDIAN_STATES_AND_UTS } from "../../helpers/clubBilling";

/**
 * Billing / invoice details for club website editor (not shown on public mini-site).
 * All editable fields are required before save.
 * @param {{
 *   t: (key: string) => string,
 *   clubName: string,
 *   values: Record<string, string>,
 *   errors?: Record<string, string>,
 *   disabled?: boolean,
 *   onFieldChange: (key: string, value: string) => void,
 *   onFieldBlur?: (key: string) => void,
 * }} props
 */
const ClubBillingFields = ({
  t,
  clubName,
  values,
  errors = {},
  disabled = false,
  onFieldChange,
  onFieldBlur,
}) => {
  const err = (key) => errors[key];
  const requiredMark = (
    <span className="text-danger" aria-hidden="true">
      {" "}
      *
    </span>
  );

  return (
    <section className="club-website-section club-billing-section">
      <h3 className="h5 club-website-section-title">
        {t("CLUB_BILLING_SECTION_TITLE")}
        {requiredMark}
      </h3>
      <p className="text-muted small mb-3">{t("CLUB_BILLING_SECTION_SUBTITLE")}</p>

      <Form.Group className="mb-3" controlId="club-billing-display-name">
        <Form.Label>{t("CLUB_BILLING_DISPLAY_NAME")}</Form.Label>
        <Form.Control value={clubName} readOnly />
        <Form.Text className="text-muted">{t("CLUB_BILLING_DISPLAY_NAME_HELP")}</Form.Text>
      </Form.Group>

      <Form.Group className="mb-3" controlId="club-billing-legal-name">
        <Form.Label>
          {t("CLUB_BILLING_LEGAL_NAME")}
          {requiredMark}
        </Form.Label>
        <Form.Control
          value={values.legalName ?? ""}
          maxLength={300}
          required
          disabled={disabled}
          isInvalid={Boolean(err("legalName"))}
          onChange={(e) => onFieldChange("legalName", e.target.value)}
          onBlur={() => onFieldBlur?.("legalName")}
        />
        <Form.Text className="text-muted">{t("CLUB_BILLING_LEGAL_NAME_HELP")}</Form.Text>
        <Form.Control.Feedback type="invalid">{err("legalName")}</Form.Control.Feedback>
      </Form.Group>

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <Form.Group controlId="club-billing-gstin">
            <Form.Label>
              {t("CLUB_BILLING_GSTIN")}
              {requiredMark}
            </Form.Label>
            <Form.Control
              value={values.gstin ?? ""}
              maxLength={15}
              required
              disabled={disabled}
              isInvalid={Boolean(err("gstin"))}
              onChange={(e) => onFieldChange("gstin", e.target.value)}
              onBlur={() => onFieldBlur?.("gstin")}
              autoCapitalize="characters"
              spellCheck={false}
            />
            <Form.Text className="text-muted">{t("CLUB_BILLING_GSTIN_HELP")}</Form.Text>
            <Form.Control.Feedback type="invalid">{err("gstin")}</Form.Control.Feedback>
          </Form.Group>
        </div>
        <div className="col-md-6">
          <Form.Group controlId="club-billing-pan">
            <Form.Label>
              {t("CLUB_BILLING_PAN")}
              {requiredMark}
            </Form.Label>
            <Form.Control
              value={values.pan ?? ""}
              maxLength={10}
              required
              disabled={disabled}
              isInvalid={Boolean(err("pan"))}
              onChange={(e) => onFieldChange("pan", e.target.value)}
              onBlur={() => onFieldBlur?.("pan")}
              autoCapitalize="characters"
              spellCheck={false}
            />
            <Form.Text className="text-muted">{t("CLUB_BILLING_PAN_HELP")}</Form.Text>
            <Form.Control.Feedback type="invalid">{err("pan")}</Form.Control.Feedback>
          </Form.Group>
        </div>
      </div>

      <Form.Group className="mb-3" controlId="club-billing-address">
        <Form.Label>
          {t("CLUB_BILLING_ADDRESS")}
          {requiredMark}
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={values.billingAddress ?? ""}
          maxLength={1000}
          required
          disabled={disabled}
          isInvalid={Boolean(err("billingAddress"))}
          onChange={(e) => onFieldChange("billingAddress", e.target.value)}
          onBlur={() => onFieldBlur?.("billingAddress")}
        />
        <Form.Control.Feedback type="invalid">
          {err("billingAddress")}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-0" controlId="club-billing-state">
        <Form.Label>
          {t("CLUB_BILLING_STATE")}
          {requiredMark}
        </Form.Label>
        <Form.Control
          list="club-billing-state-options"
          value={values.state ?? ""}
          maxLength={120}
          required
          disabled={disabled}
          isInvalid={Boolean(err("state"))}
          onChange={(e) => onFieldChange("state", e.target.value)}
          onBlur={() => onFieldBlur?.("state")}
          placeholder={t("CLUB_BILLING_STATE_PH")}
        />
        <datalist id="club-billing-state-options">
          {INDIAN_STATES_AND_UTS.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
        <Form.Text className="text-muted">{t("CLUB_BILLING_STATE_HELP")}</Form.Text>
        <Form.Control.Feedback type="invalid">{err("state")}</Form.Control.Feedback>
      </Form.Group>
    </section>
  );
};

export default ClubBillingFields;
